import { useEffect, useRef } from 'react';
import { IText } from 'fabric';

const OFFSET = 20; // marge minimale depuis le bord du canvas

/**
 * useWhiteboardText
 * - actif quand `isTextMode === true`
 * - useEffect re-bind les listeners à chaque changement
 * - double-clic sur IText existant = édition, même hors mode texte
 */
export function useWhiteboardText(fabricCanvasRef, isTextMode, getOptions, onStartEditing, socket,
  discussionId, userId) {

  // ── Mode texte actif : clic sur canvas vide = nouvel IText ────────────────
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    if (!isTextMode) return;

    canvas.isDrawingMode = false;
    canvas.selection     = true;
    canvas.skipTargetFind = false;
    canvas.defaultCursor = 'text';
    canvas.hoverCursor   = 'text';

    const handler = (opt) => {
      // Clic sur un IText existant → édition
      if (opt.target) {
        const t = opt.target;
        if (t.type === 'i-text' || t.type === 'textbox') {
          canvas.setActiveObject(t);
          requestAnimationFrame(() => {  onStartEditing?.(); t.enterEditing(); canvas.renderAll(); });
        }
        return;
      }

      const p = opt.pointer ?? opt.absolutePointer
        ?? { x: opt.e?.offsetX ?? 0, y: opt.e?.offsetY ?? 0 };

      const { color, fontSize, bold, italic } = getOptions();

      const itext = new IText('', {
        left:       Math.max(OFFSET, p.x),
        top:        Math.max(OFFSET, p.y),
        fill:       color,
        fontSize,
        fontWeight: bold   ? 'bold'   : 'normal',
        fontStyle:  italic ? 'italic' : 'normal',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        selectable: true, 
	evented: true, 
	editable: true,
	objectCaching: false,
      });

      canvas.add(itext);
      canvas.setActiveObject(itext);
      requestAnimationFrame(() => {  onStartEditing?.(); itext.enterEditing(); canvas.renderAll(); });

      itext.on('editing:exited', () => {
  if (itext.__fromRemote) return;

  if (!itext.text?.trim()) {
    canvas.remove(itext);

    socket.emit("object:removed", {
      discussionId,
      userId,
      objectId: itext.id,
    });

    return;
  }

  itext.set("id", itext.id || crypto.randomUUID());

  
socket.emit("object:modified", {
    discussionId,
    userId,
    object: itext.toObject(["id"]),
  });

});
    };

    canvas.on('mouse:down', handler);
    return () => {
      canvas.off('mouse:down', handler);
      canvas.defaultCursor = 'default';
      canvas.hoverCursor   = 'move';
      canvas.selection     = false;
    };
  }, [fabricCanvasRef, isTextMode, getOptions]);

  // ── Toujours : double-clic sur IText = édition (même hors mode texte) ─────
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const handler = (opt) => {
      if (!opt.target) return;
      const t = opt.target;
      if (t.type === 'i-text' || t.type === 'textbox') {
        canvas.setActiveObject(t);
        requestAnimationFrame(() => {  onStartEditing?.(); t.enterEditing(); canvas.renderAll(); });
      }
    };

    canvas.on('mouse:dblclick', handler);
    return () => canvas.off('mouse:dblclick', handler);
  }, [fabricCanvasRef]);
}
